<template>
  <div class="file-log-step" :class="{ 'is-disabled': isDisabled }">
    <div v-if="isDisabled" class="disabled-overlay">
      <p>{{ $t('forwarders.moduleNotSelected') }}</p>
    </div>

    <div class="form-section">
      <!-- Common Configuration -->
      <div class="common-config">
        <div class="field-row">
          <div class="field">
            <label class="label">
              {{ $t('forwarders.timeInterval') }}
              <span class="required-mark">*</span>
              <span 
                class="info-icon clickable" 
                @click="showIntervalTooltip = !showIntervalTooltip"
              >(!)</span>
              <div v-if="showIntervalTooltip" class="tooltip-content">
                <p><strong>{{ $t('forwarders.timeIntervalRules') }}:</strong></p>
                <ul>
                  <li>{{ $t('forwarders.timeIntervalRuleMin') }}</li>
                  <li>{{ $t('forwarders.timeIntervalRuleMax') }}</li>
                </ul>
              </div>
            </label>
            <div class="control">
              <input
                v-model.number="localValue.log_send_interval_seconds"
                class="input"
                :class="{ 'is-danger': intervalError }"
                type="number"
                min="3"
                max="86400"
                :placeholder="$t('forwarders.enterTimeInterval')"
                @input="validateInterval"
                @blur="handleIntervalBlur"
              >
            </div>
            <p v-if="!intervalError" class="help">{{ $t('forwarders.timeIntervalUnit') }} (3 - 86400)</p>
            <p v-if="intervalError" class="help is-danger">{{ intervalError }}</p>
          </div>

          <div class="field">
            <label class="label">
              {{ $t('forwarders.logCount') }}
              <span class="required-mark">*</span>
              <span 
                class="info-icon clickable" 
                @click="showLogCountTooltip = !showLogCountTooltip"
              >(!)</span>
              <div v-if="showLogCountTooltip" class="tooltip-content">
                <p><strong>{{ $t('forwarders.logCountRules') }}:</strong></p>
                <ul>
                  <li>{{ $t('forwarders.logCountRuleMin') }}</li>
                  <li>{{ $t('forwarders.logCountRuleMax') }}</li>
                </ul>
              </div>
            </label>
            <div class="control">
              <input
                v-model.number="localValue.batch_size"
                class="input"
                :class="{ 'is-danger': batchSizeError }"
                type="number"
                min="1"
                max="999999"
                :placeholder="$t('forwarders.enterLogCount')"
                @input="validateBatchSize"
                @blur="handleBatchSizeBlur"
              >
            </div>
            <p v-if="!batchSizeError" class="help">(1 - 999999)</p>
            <p v-if="batchSizeError" class="help is-danger">{{ batchSizeError }}</p>
          </div>
        </div>
      </div>

      <!-- OS Tabs -->
      <div class="os-tabs">
        <div class="tab-headers">
          <div
            class="tab-header"
            :class="{ 'active': activeTab === 'windows' }"
            @click="activeTab = 'windows'"
          >
            <octicon :icon="icons.deviceDesktop" />
            <span>Windows</span>
          </div>
          <div
            class="tab-header"
            :class="{ 'active': activeTab === 'linux' }"
            @click="activeTab = 'linux'"
          >
            <octicon :icon="icons.server" />
            <span>Linux</span>
          </div>
        </div>

        <div class="tab-content">
          <!-- Windows Tab -->
          <div v-if="activeTab === 'windows'" class="tab-pane">
            <div class="enable-checkbox">
              <input
                id="windows-file-enabled"
                v-model="localValue.windows.enabled"
                type="checkbox"
                class="checkbox"
                :disabled="isDisabled"
              >
              <label for="windows-file-enabled" class="checkbox-label">
                {{ $t('forwarders.enableWindows') }}
              </label>
            </div>

            <div v-if="localValue.windows.enabled" class="os-config">
              <div class="streams-section">
                <div v-if="localValue.windows.streams.length === 0" class="empty-state">
                  <p>{{ $t('forwarders.noStreamsConfigured') }}</p>
                  <button
                    type="button"
                    class="button is-primary"
                    @click="addWindowsStream"
                  >
                    <octicon :icon="icons.plus" />
                    <span>{{ $t('forwarders.addStream') }}</span>
                  </button>
                </div>

                <div v-else class="streams-tabs">
                  <div class="stream-tab-headers">
                    <div
                      v-for="(stream, index) in localValue.windows.streams"
                      :key="index"
                      class="stream-tab-header"
                      :class="{
                        'active': activeWindowsStream === index,
                        'disabled': !isCurrentWindowsStreamValid && activeWindowsStream !== index
                      }"
                      @click="switchWindowsStream(index)"
                    >
                      <span>{{ stream.name || `${$t('forwarders.stream')} ${index + 1}` }}</span>
                      <button
                        type="button"
                        class="remove-tab-btn"
                        @click.stop="removeWindowsStream(index)"
                        :title="$t('remove')"
                      >
                        <octicon :icon="icons.x" />
                      </button>
                    </div>
                    <button
                      type="button"
                      class="add-stream-tab-btn"
                      :disabled="!isCurrentWindowsStreamValid"
                      @click="addWindowsStream"
                      :title="$t('forwarders.addStream')"
                    >
                      <octicon :icon="icons.plus" />
                    </button>
                  </div>

                  <div
                    v-for="(stream, index) in localValue.windows.streams"
                    v-show="activeWindowsStream === index"
                    :key="index"
                    class="stream-tab-content"
                  >
                    <div class="stream-fields">
                    <div class="field">
                      <label class="label">
                        {{ $t('forwarders.streamName') }}
                        <span class="required-mark">*</span>
                      </label>
                      <div class="control">
                        <input
                          :ref="`windowsStreamName${index}`"
                          v-model="stream.name"
                          class="input"
                          :class="{ 'is-danger': getWindowsStreamNameError(index) }"
                          type="text"
                          maxlength="30"
                          :placeholder="$t('forwarders.enterStreamName')"
                          @blur="handleWindowsStreamNameBlur(index)"
                        >
                      </div>
                      <p v-if="!getWindowsStreamNameError(index)" class="help">({{ $t('forwarders.streamNameMaxLength') }})</p>
                      <p v-else class="help is-danger">{{ getWindowsStreamNameError(index) }}</p>
                    </div>

                    <div class="field">
                      <label class="label">
                        {{ $t('forwarders.filePaths') }}
                        <span class="required-mark">*</span>
                        <span 
                          class="info-icon clickable" 
                          @click="showWindowsFilePathsTooltip = !showWindowsFilePathsTooltip"
                        >(?)</span>
                        <div v-if="showWindowsFilePathsTooltip" class="tooltip-content">
                          <p>{{ $t('forwarders.filePathsTooltip') }}</p>
                        </div>
                      </label>
                      <div class="control">
                        <v-select
                          v-model="stream.paths"
                          multiple
                          taggable
                          no-drop
                          push-tags
                          :placeholder="$t('forwarders.enterFilePaths')"
                          @input="handleWindowsFilePathsInput(index)"
                          @search:blur="handleWindowsFilePathsBlur(index)"
                          @search:focus="handleWindowsFilePathsFocus(index)"
                        />
                      </div>
                      <p v-if="getWindowsFilePathsError(index)" class="help is-danger">{{ getWindowsFilePathsError(index) }}</p>
                    </div>

                    <div class="field">
                      <label class="label">
                        {{ $t('forwarders.excludePaths') }}
                        <span 
                          class="info-icon clickable" 
                          @click="showWindowsExcludePathsTooltip = !showWindowsExcludePathsTooltip"
                        >(?)</span>
                        <div v-if="showWindowsExcludePathsTooltip" class="tooltip-content">
                          <p>{{ $t('forwarders.excludePathsTooltip') }}</p>
                        </div>
                      </label>
                      <div class="control">
                        <v-select
                          v-model="stream.excludes"
                          multiple
                          taggable
                          no-drop
                          push-tags
                          :placeholder="$t('forwarders.enterExcludePaths')"
                          @input="handleWindowsExcludePathsInput(index)"
                        />
                      </div>
                    </div>

                    <div class="field">
                      <label class="label">{{ $t('index') }} <span class="required-mark">*</span></label>
                      <div class="control">
                        <v-select
                          v-model="stream.index_id"
                          :options="indexOptions"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="$t('forwarders.selectIndex')"
                          :filterable="true"
                          :clearable="false"
                        >
                          <template #no-options>
                            {{ $t('forwarders.noIndexAvailable') }}
                          </template>
                        </v-select>
                      </div>
                    </div>

                    <div class="field">
                      <label class="label">{{ $t('forwarders.sourceType') }} <span class="required-mark">*</span></label>
                      <div class="control">
                        <v-select
                          v-model="stream.source_type_id"
                          :options="fileLogSourceTypes"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="$t('forwarders.selectSourceType')"
                          :filterable="true"
                          :clearable="false"
                        >
                          <template #no-options>
                            {{ $t('forwarders.noSourceTypeAvailable') }}
                          </template>
                        </v-select>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Linux Tab -->
          <div v-if="activeTab === 'linux'" class="tab-pane">
            <div class="enable-checkbox">
              <input
                id="linux-file-enabled"
                v-model="localValue.linux.enabled"
                type="checkbox"
                class="checkbox"
                :disabled="isDisabled"
              >
              <label for="linux-file-enabled" class="checkbox-label">
                {{ $t('forwarders.enableLinux') }}
              </label>
            </div>

            <div v-if="localValue.linux.enabled" class="os-config">
              <div class="streams-section">
                <div v-if="localValue.linux.streams.length === 0" class="empty-state">
                  <p>{{ $t('forwarders.noStreamsConfigured') }}</p>
                  <button
                    type="button"
                    class="button is-primary"
                    @click="addLinuxStream"
                  >
                    <octicon :icon="icons.plus" />
                    <span>{{ $t('forwarders.addStream') }}</span>
                  </button>
                </div>

                <div v-else class="streams-tabs">
                  <div class="stream-tab-headers">
                    <div
                      v-for="(stream, index) in localValue.linux.streams"
                      :key="index"
                      class="stream-tab-header"
                      :class="{
                        'active': activeLinuxStream === index,
                        'disabled': !isCurrentLinuxStreamValid && activeLinuxStream !== index
                      }"
                      @click="switchLinuxStream(index)"
                    >
                      <span>{{ stream.name || `${$t('forwarders.stream')} ${index + 1}` }}</span>
                      <button
                        type="button"
                        class="remove-tab-btn"
                        @click.stop="removeLinuxStream(index)"
                        :title="$t('remove')"
                      >
                        <octicon :icon="icons.x" />
                      </button>
                    </div>
                    <button
                      type="button"
                      class="add-stream-tab-btn"
                      :disabled="!isCurrentLinuxStreamValid"
                      @click="addLinuxStream"
                      :title="$t('forwarders.addStream')"
                    >
                      <octicon :icon="icons.plus" />
                    </button>
                  </div>

                  <div
                    v-for="(stream, index) in localValue.linux.streams"
                    v-show="activeLinuxStream === index"
                    :key="index"
                    class="stream-tab-content"
                  >
                    <div class="stream-fields">
                    <div class="field">
                      <label class="label">
                        {{ $t('forwarders.streamName') }}
                        <span class="required-mark">*</span>
                      </label>
                      <div class="control">
                        <input
                          :ref="`linuxStreamName${index}`"
                          v-model="stream.name"
                          class="input"
                          :class="{ 'is-danger': getLinuxStreamNameError(index) }"
                          type="text"
                          maxlength="30"
                          :placeholder="$t('forwarders.enterStreamName')"
                          @blur="handleLinuxStreamNameBlur(index)"
                        >
                      </div>
                      <p v-if="!getLinuxStreamNameError(index)" class="help">({{ $t('forwarders.streamNameMaxLength') }})</p>
                      <p v-else class="help is-danger">{{ getLinuxStreamNameError(index) }}</p>
                    </div>

                    <div class="field">
                      <label class="label">
                        {{ $t('forwarders.filePaths') }}
                        <span class="required-mark">*</span>
                        <span 
                          class="info-icon clickable" 
                          @click="showLinuxFilePathsTooltip = !showLinuxFilePathsTooltip"
                        >(?)</span>
                        <div v-if="showLinuxFilePathsTooltip" class="tooltip-content">
                          <p>{{ $t('forwarders.filePathsTooltip') }}</p>
                        </div>
                      </label>
                      <div class="control">
                        <v-select
                          v-model="stream.paths"
                          multiple
                          taggable
                          no-drop
                          push-tags
                          :placeholder="$t('forwarders.enterFilePaths')"
                          @input="handleLinuxFilePathsInput(index)"
                          @search:blur="handleLinuxFilePathsBlur(index)"
                          @search:focus="handleLinuxFilePathsFocus(index)"
                        />
                      </div>
                      <p v-if="getLinuxFilePathsError(index)" class="help is-danger">{{ getLinuxFilePathsError(index) }}</p>
                    </div>

                    <div class="field">
                      <label class="label">
                        {{ $t('forwarders.excludePaths') }}
                        <span 
                          class="info-icon clickable" 
                          @click="showLinuxExcludePathsTooltip = !showLinuxExcludePathsTooltip"
                        >(?)</span>
                        <div v-if="showLinuxExcludePathsTooltip" class="tooltip-content">
                          <p>{{ $t('forwarders.excludePathsTooltip') }}</p>
                        </div>
                      </label>
                      <div class="control">
                        <v-select
                          v-model="stream.excludes"
                          multiple
                          taggable
                          no-drop
                          push-tags
                          :placeholder="$t('forwarders.enterExcludePaths')"
                          @input="handleLinuxExcludePathsInput(index)"
                        />
                      </div>
                    </div>

                    <div class="field">
                      <label class="label">{{ $t('index') }} <span class="required-mark">*</span></label>
                      <div class="control">
                        <v-select
                          v-model="stream.index_id"
                          :options="indexOptions"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="$t('forwarders.selectIndex')"
                          :filterable="true"
                          :clearable="false"
                        >
                          <template #no-options>
                            {{ $t('forwarders.noIndexAvailable') }}
                          </template>
                        </v-select>
                      </div>
                    </div>

                    <div class="field">
                      <label class="label">{{ $t('forwarders.sourceType') }} <span class="required-mark">*</span></label>
                      <div class="control">
                        <v-select
                          v-model="stream.source_type_id"
                          :options="fileLogSourceTypes"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="$t('forwarders.selectSourceType')"
                          :filterable="true"
                          :clearable="false"
                        >
                          <template #no-options>
                            {{ $t('forwarders.noSourceTypeAvailable') }}
                          </template>
                        </v-select>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as icons from 'octicons-vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'FileLogStep',
  components: {
    vSelect
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    indexNames: {
      type: [Object, Array],
      default: () => ({})
    },
    fileLogSourceTypes: {
      type: Array,
      default: () => []
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localValue: { ...this.value },
      activeTab: 'windows',
      activeWindowsStream: 0,
      activeLinuxStream: 0,
      emitTimeout: null,
      intervalError: '',
      batchSizeError: '',
      showIntervalTooltip: false,
      showLogCountTooltip: false,
      showWindowsFilePathsTooltip: false,
      showWindowsExcludePathsTooltip: false,
      showLinuxFilePathsTooltip: false,
      showLinuxExcludePathsTooltip: false,
      touchedWindowsStreams: [],
      touchedLinuxStreams: [],
      touchedWindowsFilePaths: [],
      touchedLinuxFilePaths: []
    }
  },
  computed: {
    icons() {
      return icons
    },
    indexOptions() {
      return Array.isArray(this.indexNames)
        ? this.indexNames
        : Object.values(this.indexNames || {})
    },
    isCurrentWindowsStreamValid() {
      // Check if current Windows stream has all required fields filled
      if (this.localValue.windows.streams.length === 0) {
        return true
      }
      const currentStream = this.localValue.windows.streams[this.activeWindowsStream]
      if (!currentStream) {
        return true
      }
      return (
        currentStream.name &&
        currentStream.name.trim() !== '' &&
        !this.getWindowsStreamNameError(this.activeWindowsStream) &&
        currentStream.paths &&
        currentStream.paths.length > 0 &&
        currentStream.index_id &&
        currentStream.source_type_id
      )
    },
    isCurrentLinuxStreamValid() {
      // Check if current Linux stream has all required fields filled
      if (this.localValue.linux.streams.length === 0) {
        return true
      }
      const currentStream = this.localValue.linux.streams[this.activeLinuxStream]
      if (!currentStream) {
        return true
      }
      return (
        currentStream.name &&
        currentStream.name.trim() !== '' &&
        !this.getLinuxStreamNameError(this.activeLinuxStream) &&
        currentStream.paths &&
        currentStream.paths.length > 0 &&
        currentStream.index_id &&
        currentStream.source_type_id
      )
    }
  },
  watch: {
    value: {
      handler(newVal) {
        // Avoid deep clone on every change - only update if really needed
        if (JSON.stringify(this.localValue) !== JSON.stringify(newVal)) {
          this.localValue = JSON.parse(JSON.stringify(newVal))
        }
      },
      deep: true
    },
    localValue: {
      handler(newVal) {
        // Debounce emit to avoid excessive updates
        if (this.emitTimeout) {
          clearTimeout(this.emitTimeout)
        }
        this.emitTimeout = setTimeout(() => {
          this.$emit('input', newVal)
        }, 100)
      },
      deep: true
    },
    'localValue.windows.enabled'(newVal) {
      // Auto-create default stream when Windows is enabled and no streams exist
      if (newVal && this.localValue.windows.streams.length === 0) {
        this.addWindowsStream()
      }
    },
    'localValue.linux.enabled'(newVal) {
      // Auto-create default stream when Linux is enabled and no streams exist
      if (newVal && this.localValue.linux.streams.length === 0) {
        this.addLinuxStream()
      }
    }
  },
  mounted() {
    // Validate initial values
    this.validateInterval()
    this.validateBatchSize()
    // Add click outside listener for tooltips
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    if (this.emitTimeout) {
      clearTimeout(this.emitTimeout)
    }
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    switchWindowsStream(index) {
      // Only allow switching if current stream is valid or switching to the same stream
      if (index === this.activeWindowsStream) {
        return
      }
      if (!this.isCurrentWindowsStreamValid) {
        return
      }
      this.activeWindowsStream = index
    },
    switchLinuxStream(index) {
      // Only allow switching if current stream is valid or switching to the same stream
      if (index === this.activeLinuxStream) {
        return
      }
      if (!this.isCurrentLinuxStreamValid) {
        return
      }
      this.activeLinuxStream = index
    },
    addWindowsStream() {
      this.localValue.windows.streams.push({
        name: '',
        paths: [],
        excludes: [],
        index_id: '',
        source_type_id: ''
      })
      // Switch to the newly added stream
      this.activeWindowsStream = this.localValue.windows.streams.length - 1
    },
    removeWindowsStream(index) {
      this.localValue.windows.streams.splice(index, 1)
      // Adjust active stream index if needed
      if (this.activeWindowsStream >= this.localValue.windows.streams.length) {
        this.activeWindowsStream = Math.max(0, this.localValue.windows.streams.length - 1)
      }
    },
    addLinuxStream() {
      this.localValue.linux.streams.push({
        name: '',
        paths: [],
        excludes: [],
        index_id: '',
        source_type_id: ''
      })
      // Switch to the newly added stream
      this.activeLinuxStream = this.localValue.linux.streams.length - 1
    },
    removeLinuxStream(index) {
      this.localValue.linux.streams.splice(index, 1)
      // Adjust active stream index if needed
      if (this.activeLinuxStream >= this.localValue.linux.streams.length) {
        this.activeLinuxStream = Math.max(0, this.localValue.linux.streams.length - 1)
      }
    },
    validateInterval() {
      const value = this.localValue.log_send_interval_seconds
      
      if (value < 3) {
        this.intervalError = this.$t('forwarders.intervalRangeError')
        return false
      }
      
      if (value > 86400) {
        this.intervalError = this.$t('forwarders.intervalRangeError')
        return false
      }
      
      this.intervalError = ''
      return true
    },
    validateBatchSize() {
      const value = this.localValue.batch_size

      if (value < 1) {
        this.batchSizeError = this.$t('forwarders.batchSizeRangeError')
        return false
      }

      if (value > 999999) {
        this.batchSizeError = this.$t('forwarders.batchSizeRangeError')
        return false
      }

      this.batchSizeError = ''
      return true
    },
    getWindowsStreamNameError(index) {
      const stream = this.localValue.windows.streams[index]
      
      // Check if field is empty or only contains spaces
      if (!stream || !stream.name || stream.name.trim() === '') {
        if (this.touchedWindowsStreams.includes(index)) {
          // If has content but only spaces, show invalid format error
          if (stream && stream.name && stream.name.length > 0 && stream.name.trim() === '') {
            return this.$t('forwarders.streamNameInvalid')
          }
          // Otherwise show required error
          return this.$t('forwarders.streamNameRequired')
        }
        return ''
      }

      // Check if stream name is duplicated in Windows streams
      const trimmedName = stream.name.trim()
      const duplicateIndex = this.localValue.windows.streams.findIndex((s, i) =>
        i !== index && s.name && s.name.trim() === trimmedName
      )

      if (duplicateIndex !== -1) {
        return this.$t('forwarders.streamNameDuplicate')
      }

      return ''
    },
    getLinuxStreamNameError(index) {
      const stream = this.localValue.linux.streams[index]
      
      // Check if field is empty or only contains spaces
      if (!stream || !stream.name || stream.name.trim() === '') {
        if (this.touchedLinuxStreams.includes(index)) {
          // If has content but only spaces, show invalid format error
          if (stream && stream.name && stream.name.length > 0 && stream.name.trim() === '') {
            return this.$t('forwarders.streamNameInvalid')
          }
          // Otherwise show required error
          return this.$t('forwarders.streamNameRequired')
        }
        return ''
      }

      // Check if stream name is duplicated in Linux streams
      const trimmedName = stream.name.trim()
      const duplicateIndex = this.localValue.linux.streams.findIndex((s, i) =>
        i !== index && s.name && s.name.trim() === trimmedName
      )

      if (duplicateIndex !== -1) {
        return this.$t('forwarders.streamNameDuplicate')
      }

      return ''
    },
    handleWindowsStreamNameBlur(index) {
      if (!this.touchedWindowsStreams.includes(index)) {
        this.touchedWindowsStreams.push(index)
      }
      
      // Focus back to input if empty
      const stream = this.localValue.windows.streams[index]
      if (!stream || !stream.name || stream.name.trim() === '') {
        this.$nextTick(() => {
          const inputRef = this.$refs[`windowsStreamName${index}`]
          if (inputRef && inputRef.length > 0) {
            inputRef[0].focus()
          } else if (inputRef) {
            inputRef.focus()
          }
        })
      }
    },
    handleLinuxStreamNameBlur(index) {
      if (!this.touchedLinuxStreams.includes(index)) {
        this.touchedLinuxStreams.push(index)
      }
      
      // Focus back to input if empty
      const stream = this.localValue.linux.streams[index]
      if (!stream || !stream.name || stream.name.trim() === '') {
        this.$nextTick(() => {
          const inputRef = this.$refs[`linuxStreamName${index}`]
          if (inputRef && inputRef.length > 0) {
            inputRef[0].focus()
          } else if (inputRef) {
            inputRef.focus()
          }
        })
      }
    },
    getWindowsFilePathsError(index) {
      const stream = this.localValue.windows.streams[index]
      if (!stream || !stream.paths || stream.paths.length === 0) {
        if (this.touchedWindowsFilePaths.includes(index)) {
          return this.$t('forwarders.filePathsRequired')
        }
      }
      return ''
    },
    getLinuxFilePathsError(index) {
      const stream = this.localValue.linux.streams[index]
      if (!stream || !stream.paths || stream.paths.length === 0) {
        if (this.touchedLinuxFilePaths.includes(index)) {
          return this.$t('forwarders.filePathsRequired')
        }
      }
      return ''
    },
    handleWindowsFilePathsInput(index) {
      const stream = this.localValue.windows.streams[index]
      if (stream && stream.paths && stream.paths.length > 0) {
        // Limit each path to 4096 characters
        stream.paths = stream.paths.map(path => {
          if (typeof path === 'string' && path.length > 4096) {
            return path.substring(0, 4096)
          }
          return path
        })
      }
    },
    handleLinuxFilePathsInput(index) {
      const stream = this.localValue.linux.streams[index]
      if (stream && stream.paths && stream.paths.length > 0) {
        // Limit each path to 4096 characters
        stream.paths = stream.paths.map(path => {
          if (typeof path === 'string' && path.length > 4096) {
            return path.substring(0, 4096)
          }
          return path
        })
      }
    },
    handleWindowsFilePathsFocus(index) {
      // Mark as touched when focused
      if (!this.touchedWindowsFilePaths.includes(index)) {
        this.touchedWindowsFilePaths.push(index)
      }
    },
    handleWindowsFilePathsBlur(index) {
      // Force validation on blur
      this.$forceUpdate()
    },
    handleLinuxFilePathsFocus(index) {
      // Mark as touched when focused
      if (!this.touchedLinuxFilePaths.includes(index)) {
        this.touchedLinuxFilePaths.push(index)
      }
    },
    handleLinuxFilePathsBlur(index) {
      // Force validation on blur
      this.$forceUpdate()
    },
    handleWindowsExcludePathsInput(index) {
      const stream = this.localValue.windows.streams[index]
      if (stream && stream.excludes && stream.excludes.length > 0) {
        // Limit each path to 4096 characters
        stream.excludes = stream.excludes.map(path => {
          if (typeof path === 'string' && path.length > 4096) {
            return path.substring(0, 4096)
          }
          return path
        })
      }
    },
    handleLinuxExcludePathsInput(index) {
      const stream = this.localValue.linux.streams[index]
      if (stream && stream.excludes && stream.excludes.length > 0) {
        // Limit each path to 4096 characters
        stream.excludes = stream.excludes.map(path => {
          if (typeof path === 'string' && path.length > 4096) {
            return path.substring(0, 4096)
          }
          return path
        })
      }
    },
    handleClickOutside(event) {
      // Check if click is outside tooltip areas
      const tooltipTriggers = this.$el.querySelectorAll('.info-icon.clickable')
      const tooltipContents = this.$el.querySelectorAll('.tooltip-content')
      
      let clickedInsideTooltip = false
      
      // Check if clicked on trigger or content
      tooltipTriggers.forEach(trigger => {
        if (trigger.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })
      
      tooltipContents.forEach(content => {
        if (content.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })
      
      // If clicked outside, close all tooltips
      if (!clickedInsideTooltip) {
        this.showIntervalTooltip = false
        this.showLogCountTooltip = false
        this.showWindowsFilePathsTooltip = false
        this.showWindowsExcludePathsTooltip = false
        this.showLinuxFilePathsTooltip = false
        this.showLinuxExcludePathsTooltip = false
      }
    },
    handleIntervalBlur() {
      const value = this.localValue.log_send_interval_seconds
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.log_send_interval_seconds = 15
      } else if (value < 3) {
        // Set to minimum value if less than 3
        this.localValue.log_send_interval_seconds = 3
      } else if (value > 86400) {
        // Set to maximum value if greater than 86400
        this.localValue.log_send_interval_seconds = 86400
      }
      this.intervalError = ''
    },
    handleBatchSizeBlur() {
      const value = this.localValue.batch_size
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.batch_size = 100
      } else if (value < 1) {
        // Set to minimum value if less than 1
        this.localValue.batch_size = 1
      } else if (value > 999999) {
        // Set to maximum value if greater than 999999
        this.localValue.batch_size = 999999
      }
      this.batchSizeError = ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/dark-modal.scss';

.file-log-step {
  position: relative;

  &.is-disabled {
    opacity: 0.5;
    pointer-events: none;

    .disabled-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgba(26, 29, 46, 0.95);
      padding: 2rem;
      border-radius: 8px;
      text-align: center;
      z-index: 10;
      pointer-events: auto;

      p {
        color: #fff;
        font-size: 1rem;
        margin: 0;
      }
    }
  }

  .form-section {
    max-width: 1200px;
  }

  .common-config {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background-color: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
  }

  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .os-tabs {
    .tab-headers {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
      border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    }

    .tab-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.6);
      font-weight: 500;
      border-bottom: 2px solid transparent;
      margin-bottom: -2px;
      transition: all 0.2s;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }

      &.active {
        color: #1F6FF4;
        border-bottom-color: #1F6FF4;
      }
    }

    .tab-content {
      padding: 0.75rem;
      background-color: rgba(255, 255, 255, 0.03);
      border-radius: 8px;
    }

    .tab-pane {
      .enable-checkbox {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        .checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #1F6FF4;
        }

        .checkbox-label {
          color: $modal-text;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          margin: 0;
        }
      }

      .os-config {
        padding-top: 0.5rem;
      }
    }
  }

  .streams-section {
    .empty-state {
      padding: 3rem;
      text-align: center;
      background-color: rgba(255, 255, 255, 0.03);
      border-radius: 8px;
      border: 2px dashed rgba(255, 255, 255, 0.2);

      p {
        color: rgba(255, 255, 255, 0.6);
        margin: 0 0 1.5rem 0;
        font-size: 1rem;
      }

      .button {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1.5rem;
        border-radius: 6px;
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.2s;
        background-color: #1F6FF4;
        border: none;
        color: #fff;

        &:hover {
          background-color: #1557c7;
          transform: translateY(-1px);
        }
      }
    }

    .streams-tabs {
      .stream-tab-headers {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 2px solid rgba(255, 255, 255, 0.1);
        flex-wrap: wrap;
        padding-bottom: 0;

        .stream-tab-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          cursor: pointer;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 500;
          font-size: 0.9rem;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s;
          white-space: nowrap;
          background-color: rgba(255, 255, 255, 0.03);
          border-radius: 6px 6px 0 0;

          &:hover {
            color: rgba(255, 255, 255, 0.8);
            background-color: rgba(255, 255, 255, 0.05);
          }

          &.active {
            color: #1F6FF4;
            border-bottom-color: #1F6FF4;
            background-color: rgba(31, 111, 244, 0.1);
          }

          &.disabled {
            opacity: 0.5;
            cursor: not-allowed;

            &:hover {
              color: rgba(255, 255, 255, 0.6);
              background-color: rgba(255, 255, 255, 0.03);
            }

            .remove-tab-btn {
              pointer-events: auto;
            }
          }

          .remove-tab-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            padding: 0;
            border: none;
            background-color: rgba(255, 71, 87, 0.2);
            color: #ff4757;
            border-radius: 50%;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
              background-color: #ff4757;
              color: #fff;
            }
          }
        }

        .add-stream-tab-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
          padding: 0.75rem;
          border: none;
          background-color: rgba(31, 111, 244, 0.1);
          color: #1F6FF4;
          border-radius: 6px 6px 0 0;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: -2px;

          &:hover:not(:disabled) {
            background-color: rgba(31, 111, 244, 0.2);
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background-color: rgba(255, 255, 255, 0.05);
            color: rgba(255, 255, 255, 0.4);
          }
        }
      }

      .stream-tab-content {
        padding: 1rem;
        background-color: rgba(255, 255, 255, 0.03);
        border-radius: 8px;

        .stream-fields {
          display: grid;
          gap: 0.75rem;
        }
      }
    }
  }

  .field {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label {
    color: $modal-text;
    font-weight: 500;
    margin-bottom: 0.35rem;
    display: block;
    position: relative;

    .required-mark {
      color: #ff4757;
      margin-left: 2px;
    }

    .info-icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      line-height: 16px;
      text-align: center;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
      color: #fff;
      font-size: 0.75rem;
      margin-left: 4px;
      cursor: help;
      font-style: normal;
      transition: all 0.2s;
      
      &.clickable {
        cursor: pointer;
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }
      }
    }
    
    .tooltip-content {
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 0.5rem;
      padding: 1rem;
      background-color: #2a2d36;
      border: 1px solid #404553;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 1000;
      min-width: 250px;
      max-width: 350px;
      white-space: normal;
      
      p {
        color: #fff;
        margin: 0 0 0.5rem 0;
        font-size: 0.9rem;
        line-height: 1.5;

        strong {
          color: #1F6FF4;
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: rgba(255, 255, 255, 0.85);
          font-size: 0.85rem;
          line-height: 1.6;
          padding-left: 1rem;
          position: relative;
          margin-bottom: 0.25rem;

          &:before {
            content: "•";
            position: absolute;
            left: 0;
            color: #1F6FF4;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .control {
    .input,
    .textarea {
      background-color: $modal-input-bg;
      border: 1px solid $modal-border;
      color: #000;
      border-radius: 4px;
      padding: 0.75rem;
      width: 100%;
      font-size: 0.95rem;

      &:focus {
        border-color: #1F6FF4;
        outline: none;
      }

      &:disabled,
      &[readonly] {
        background-color: #1a1d2e;
        cursor: not-allowed;
        opacity: 0.7;
      }

      &::placeholder {
        color: rgba(0, 0, 0, 0.45);
      }

      &.is-danger {
        border-color: #ff4757;

        &:focus {
          border-color: #ff4757;
          box-shadow: 0 0 0 0.125em rgba(255, 71, 87, 0.25);
        }
      }
    }

    .textarea {
      resize: vertical;
      min-height: 60px;
      font-family: inherit;
    }
  }

  .dashboard.is-dark & {
    .control {
      .input,
      .textarea {
        color: $modal-text;

        &::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
      }
    }
  }

  .v-select {
    .area {
      resize: vertical;
      min-height: 60px;
      font-family: inherit;
    }
  }

  :deep(.stream-fields .input[type='text']) {
    color: #000 !important;
  }

  :deep(.stream-fields .vs__search),
  :deep(.stream-fields .vs__search:focus),
  :deep(.stream-fields .vs__selected),
  :deep(.stream-fields .vs__dropdown-option) {
    color: #000 !important;
  }

  :deep(.stream-fields .vs__search::placeholder) {
    color: rgba(0, 0, 0, 0.45) !important;
  }

  .dashboard.is-dark & {
    :deep(.stream-fields .input[type='text']),
    :deep(.stream-fields .vs__search),
    :deep(.stream-fields .vs__search:focus),
    :deep(.stream-fields .vs__selected),
    :deep(.stream-fields .vs__dropdown-option) {
      color: #fff !important;
    }

    :deep(.stream-fields .vs__search::placeholder) {
      color: rgba(255, 255, 255, 0.4) !important;
    }
  }

  .help {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    margin-top: 0.25rem;
    
    &.is-danger {
      color: #ff4757;
    }
  }
}
</style>
